import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Demo.css';
import {
  ArrowForwardRounded,
  BadgeOutlined,
  CheckCircleOutlineRounded,
  HighlightOffRounded,
  KeyOutlined,
  LockOutlined,
  PhoneIphoneOutlined,
  ShieldOutlined,
  TimerOutlined,
  VerifiedUserOutlined
} from '@mui/icons-material';
import config from './config';
import { checkVPN } from './utils/vpnChecker';

// Helper function to convert ArrayBuffer to Base64
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// Helper function to convert Base64 to ArrayBuffer
const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const API_BASE_URL = config.API_BASE_URL;

function Demo() {
  const navigate = useNavigate();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authResult, setAuthResult] = useState(null); // { over18: boolean, ageToken: string } or null
  const [authError, setAuthError] = useState(null);
  
  // VPN checking state for each button
  const [vpnCheckStatus, setVpnCheckStatus] = useState({
    digitalId: null, // null = not checked, 'checking' = checking, true = VPN detected, false = no VPN
    ageEstimation: null,
    idVerification: null,
    passkey: null,
  });

  // Handler for Continue buttons with VPN check
  const handleContinueWithVPNCheck = async (buttonKey, navigateTo) => {
    // If already checking or already checked and VPN detected, don't proceed
    if (vpnCheckStatus[buttonKey] === 'checking' || vpnCheckStatus[buttonKey] === true) {
      return;
    }

    // Start VPN check
    setVpnCheckStatus(prev => ({ ...prev, [buttonKey]: 'checking' }));

    try {
      const vpnResult = await checkVPN();
      
      if (vpnResult.isVPN) {
        // VPN detected - show red X and don't navigate
        setVpnCheckStatus(prev => ({ ...prev, [buttonKey]: true }));
      } else {
        // No VPN - show green tick, then navigate
        setVpnCheckStatus(prev => ({ ...prev, [buttonKey]: false }));
        
        // Wait a moment to show the green tick animation, then navigate
        setTimeout(() => {
          navigate(navigateTo);
        }, 800);
      }
    } catch (error) {
      console.error('VPN check error:', error);
      // On error, allow navigation (fail open)
      setVpnCheckStatus(prev => ({ ...prev, [buttonKey]: false }));
      setTimeout(() => {
        navigate(navigateTo);
      }, 800);
    }
  };

  // Authenticate with passkey
  const authenticateWithPasskey = async () => {
    // Check VPN first
    if (vpnCheckStatus.passkey === 'checking' || vpnCheckStatus.passkey === true) {
      return;
    }

    // Start VPN check
    setVpnCheckStatus(prev => ({ ...prev, passkey: 'checking' }));

    try {
      const vpnResult = await checkVPN();
      
      if (vpnResult.isVPN) {
        // VPN detected - show red X and don't proceed
        setVpnCheckStatus(prev => ({ ...prev, passkey: true }));
        setAuthError('VPN detected. Please disable your VPN to continue.');
        return;
      } else {
        // No VPN - continue with passkey authentication
        setVpnCheckStatus(prev => ({ ...prev, passkey: false }));
      }
    } catch (error) {
      console.error('VPN check error:', error);
      // On error, allow authentication (fail open)
      setVpnCheckStatus(prev => ({ ...prev, passkey: false }));
    }
    // Check if WebAuthn is supported
    if (!navigator.credentials || !navigator.credentials.get) {
      setAuthError('WebAuthn is not supported in this browser. Please use a modern browser.');
      return;
    }

    setIsAuthenticating(true);
    setAuthError(null);
    setAuthResult(null);

    try {
      // Get challenge from server (or generate client-side)
      // Note: We don't specify allowCredentials to let the browser discover all passkeys
      // for this domain (including synced passkeys from other devices)
      let challenge;
      try {
        const challengeResponse = await fetch(
          `${API_BASE_URL}/identifications/passkey/challenge`
        );
        if (challengeResponse.ok) {
          const challengeData = await challengeResponse.json();
          if (challengeData.challenge) {
            challenge = base64ToArrayBuffer(challengeData.challenge);
          }
        }
      } catch (e) {
        console.warn('Could not get challenge from server, generating client-side:', e);
      }

      // Generate challenge if server didn't provide one
      if (!challenge) {
        challenge = new Uint8Array(32);
        crypto.getRandomValues(challenge);
      }

      // Create WebAuthn authentication request
      // Note: We omit allowCredentials to enable cross-device passkey discovery
      // The browser will show all available passkeys for this domain, including
      // synced passkeys from other devices (e.g., MacBook passkey synced to Pixel)
      const publicKeyCredentialRequestOptions = {
        challenge: challenge,
        rpId: window.location.hostname, // Explicitly set rpId to help with passkey discovery
        // allowCredentials omitted - allows browser to discover all passkeys for domain
        userVerification: 'required',
        timeout: 60000,
      };

      // Get assertion from WebAuthn
      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
      });

      if (!assertion) {
        throw new Error('Failed to authenticate with passkey');
      }

      // Extract assertion data and convert to Base64
      const credentialId = arrayBufferToBase64(assertion.rawId);
      const authenticatorData = arrayBufferToBase64(assertion.response.authenticatorData);
      const clientDataJSON = arrayBufferToBase64(assertion.response.clientDataJSON);
      const signature = arrayBufferToBase64(assertion.response.signature);
      const userHandle = assertion.response.userHandle 
        ? arrayBufferToBase64(assertion.response.userHandle)
        : null;

      // Send to server
      const response = await fetch(`${API_BASE_URL}/identifications/passkey/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credentialId,
          authenticatorData,
          clientDataJSON,
          signature,
          userHandle,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to authenticate: ${response.statusText}`);
      }

      const data = await response.json();
      
      // The API should return over18 status and ageToken
      // Since users must be verified as over 18 to register a passkey,
      // successful authentication means they are over 18
      // But we'll check if the API explicitly returns over18
      const over18 = data.over18 !== undefined ? data.over18 : true;
      
      setAuthResult({
        over18: over18,
        ageToken: data.ageToken,
      });

      // Store the fresh age token
      if (data.ageToken) {
        localStorage.setItem('ageToken', data.ageToken);
        if (data.ageTokenExpiresAt) {
          localStorage.setItem('ageTokenExpiresAt', data.ageTokenExpiresAt);
        }
      }
    } catch (err) {
      console.error('Error authenticating with passkey:', err);
      // Handle user cancellation gracefully
      if (err.name === 'NotAllowedError' || err.message.includes('cancel')) {
        setAuthError('Authentication was cancelled. Please try again.');
      } else {
        setAuthError(err.message || 'Failed to authenticate with passkey. Please try again.');
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const renderVpnButtonContent = (status) => {
    if (status === 'checking') {
      return (
        <>
          <span className="demo-btn-spinner" aria-hidden="true" />
          <span>Checking VPN…</span>
        </>
      );
    }
    if (status === false) {
      return (
        <>
          <CheckCircleOutlineRounded className="demo-btn-statusIcon demo-btn-statusIcon--ok" />
          <span>VPN not detected</span>
        </>
      );
    }
    if (status === true) {
      return (
        <>
          <HighlightOffRounded className="demo-btn-statusIcon demo-btn-statusIcon--bad" />
          <span>VPN detected</span>
        </>
      );
    }
    return (
      <>
        <span>Continue</span>
        <ArrowForwardRounded className="demo-btn-arrow" />
      </>
    );
  };

  const renderPasskeyStatus = () => {
    if (vpnCheckStatus.passkey === 'checking') {
      return { tone: 'muted', text: 'Checking VPN status…' };
    }
    if (vpnCheckStatus.passkey === true) {
      return { tone: 'danger', text: 'VPN detected. Disable your VPN to continue.' };
    }
    if (isAuthenticating) {
      return { tone: 'muted', text: 'Authenticating with your passkey…' };
    }
    if (authResult) {
      return authResult.over18
        ? { tone: 'success', text: 'Verified: 18 or over' }
        : { tone: 'danger', text: 'Not verified: under 18' };
    }
    return { tone: 'muted', text: 'Reuse a previous age verification securely with a Veritage Key (passkey).' };
  };

  const passkeyStatus = renderPasskeyStatus();

  return (
    <div className="demo-shell">
      <div className="demo-wrap">
        <header className="demo-nav">
          <Link to="/" className="demo-brand" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            <svg
              className="demo-brand-logo"
              width="32"
              height="32"
              viewBox="0 0 200 200"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <g transform="translate(100,100)" stroke="currentColor" strokeWidth="5" strokeLinecap="round">
                <g>
                  <circle cx="0" cy="0" r="82" fill="none" stroke="none" />
                  {[...Array(20)].map((_, i) => (
                    <line
                      key={`outer-${i}`}
                      x1="0"
                      y1="-82"
                      x2="0"
                      y2="-70"
                      transform={`rotate(${i * 18})`}
                    />
                  ))}
                </g>
                <g>
                  <circle cx="0" cy="0" r="60" fill="none" stroke="none" />
                  {[...Array(20)].map((_, i) => (
                    <line
                      key={`inner-${i}`}
                      x1="0"
                      y1="-56"
                      x2="0"
                      y2="-48"
                      transform={`rotate(${i * 18})`}
                    />
                  ))}
                </g>
              </g>
            </svg>
            <span className="demo-brand-name">Veritage</span>
          </Link>

          <div className="demo-navMeta">
            <span className="demo-chip demo-chip--subtle">Enterprise demo</span>
          </div>
        </header>

        <section className="demo-hero">
          <h1 className="demo-title">Choose how you want to confirm your age</h1>
        </section>

        <section className="demo-grid" aria-label="Age confirmation methods">
          {/* 1) Age estimation */}
          <article className="demo-card">
            <div className="demo-cardHead">
              <div className="demo-iconTile" aria-hidden="true">
                <ShieldOutlined className="demo-iconTileIcon" />
              </div>
              <div className="demo-cardHeadText">
                <h2 className="demo-card-title">Age estimation</h2>
                <p className="demo-card-description">Use your camera to estimate age, no identity required.</p>
              </div>
            </div>

            <ul className="demo-featureList">
              <li>
                <TimerOutlined className="demo-featureIcon" />
                <span>Fast, low-friction check</span>
              </li>
              <li>
                <ShieldOutlined className="demo-featureIcon" />
                <span>No one sees, stores, or shares your image</span>
              </li>
              <li>
                <LockOutlined className="demo-featureIcon" />
                <span>Secure processing</span>
              </li>
            </ul>

            <div className="demo-cardActions">
              <button
                className="demo-btn demo-btn--primary"
                onClick={() => handleContinueWithVPNCheck('ageEstimation', '/age-estimation/consent')}
                disabled={vpnCheckStatus.ageEstimation === 'checking'}
              >
                {renderVpnButtonContent(vpnCheckStatus.ageEstimation)}
              </button>
              <button
                className="demo-btn demo-btn--secondary"
                type="button"
                onClick={() => navigate('/age-estimation/how-it-works')}
              >
                How it works
              </button>
            </div>
          </article>

          {/* 2) ID verification */}
          <article className="demo-card">
            <div className="demo-cardHead">
              <div className="demo-iconTile" aria-hidden="true">
                <BadgeOutlined className="demo-iconTileIcon" />
              </div>
              <div className="demo-cardHeadText">
                <h2 className="demo-card-title">ID verification</h2>
                <p className="demo-card-description">Use a passport, driving licence, or national ID.</p>
              </div>
            </div>

            <ul className="demo-featureList">
              <li>
                <BadgeOutlined className="demo-featureIcon" />
                <span>Capture or upload your document</span>
              </li>
              <li>
                <VerifiedUserOutlined className="demo-featureIcon" />
                <span>Optional face match to prevent misuse</span>
              </li>
              <li>
                <ShieldOutlined className="demo-featureIcon" />
                <span>No long-term storage of your document or selfie</span>
              </li>
            </ul>

            <div className="demo-cardActions">
              <button
                className="demo-btn demo-btn--primary"
                onClick={() => handleContinueWithVPNCheck('idVerification', '/id-verification')}
                disabled={vpnCheckStatus.idVerification === 'checking'}
              >
                {renderVpnButtonContent(vpnCheckStatus.idVerification)}
              </button>
              <button className="demo-btn demo-btn--secondary" type="button" disabled>
                How it works
              </button>
            </div>
          </article>

          {/* 3) Digital ID */}
          <article className="demo-card">
            <div className="demo-cardHead">
              <div className="demo-iconTile" aria-hidden="true">
                <PhoneIphoneOutlined className="demo-iconTileIcon" />
              </div>
              <div className="demo-cardHeadText">
                <h2 className="demo-card-title">Digital ID app</h2>
                <p className="demo-card-description">Share that you're 18+ and nothing else.</p>
              </div>
            </div>

            <ul className="demo-featureList">
              <li>
                <TimerOutlined className="demo-featureIcon" />
                <span>Instant confirmation</span>
              </li>
              <li>
                <VerifiedUserOutlined className="demo-featureIcon" />
                <span>Reusable proof across online and offline checks</span>
              </li>
              <li>
                <LockOutlined className="demo-featureIcon" />
                <span>Protected with strong encryption</span>
              </li>
            </ul>

            <div className="demo-badges">
              <span className="demo-chip demo-chip--brand">Double anonymity</span>
            </div>

            <div className="demo-cardActions">
              <button
                className="demo-btn demo-btn--primary"
                onClick={() => handleContinueWithVPNCheck('digitalId', '/digital-id')}
                disabled={vpnCheckStatus.digitalId === 'checking'}
              >
                {renderVpnButtonContent(vpnCheckStatus.digitalId)}
              </button>
              <button className="demo-btn demo-btn--secondary" type="button" disabled>
                How it works
              </button>
            </div>
          </article>

          {/* 4) Veritage Key (Passkey) */}
          <article
            className={[
              'demo-card',
              'demo-card--interactive',
              isAuthenticating ? 'demo-card--busy' : '',
              authResult ? 'demo-card--success' : '',
              vpnCheckStatus.passkey === true ? 'demo-card--danger' : ''
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label="Use an existing Veritage Key (passkey)"
          >
            <div className="demo-cardHead">
              <div className="demo-iconTile" aria-hidden="true">
                <KeyOutlined className="demo-iconTileIcon" />
              </div>
              <div className="demo-cardHeadText">
                <h2 className="demo-card-title">Veritage Key</h2>
                <p className="demo-card-description">Reuse a previous verification with a passkey.</p>
              </div>
            </div>

            <div className={`demo-status demo-status--${passkeyStatus.tone}`}>
              <span>{passkeyStatus.text}</span>
            </div>

            {authError && <div className="demo-status demo-status--danger">{authError}</div>}

            <div className="demo-cardActions">
              <button
                className="demo-btn demo-btn--primary"
                onClick={authenticateWithPasskey}
                disabled={isAuthenticating || vpnCheckStatus.passkey === 'checking'}
              >
                {vpnCheckStatus.passkey === 'checking' || isAuthenticating ? (
                  <>
                    <span className="demo-btn-spinner" aria-hidden="true" />
                    <span>Authenticating…</span>
                  </>
                ) : (
                  <>
                    <span>Use Veritage Key</span>
                    <ArrowForwardRounded className="demo-btn-arrow" />
                  </>
                )}
              </button>
              <button className="demo-btn demo-btn--secondary" type="button" disabled>
                How it works
              </button>
            </div>
          </article>
        </section>

        <footer className="demo-footer">
          <div className="demo-footerRow">
            <div className="demo-footerCompliance" aria-label="Compliance">
              <span className="demo-footerBadge">ISO/IEC 27001</span>
              <span className="demo-footerDot" aria-hidden="true">
                •
              </span>
              <span className="demo-footerBadge">GDPR</span>
              <span className="demo-footerDot" aria-hidden="true">
                •
              </span>
              <span className="demo-footerBadge">SOC 2 Type II</span>
            </div>

            <div className="demo-footerLinks" aria-label="Legal links">
              <button className="demo-footerLink" type="button" onClick={() => navigate('/privacy')}>
                Privacy
              </button>
              <button className="demo-footerLink" type="button" onClick={() => navigate('/terms')}>
                Terms
              </button>
              <button className="demo-footerLink" type="button" onClick={(e) => e.preventDefault()}>
                Security
              </button>
            </div>
          </div>

          <p className="demo-footerCopyright">© {new Date().getFullYear()} Veritage. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}

export default Demo;
